/**
 * SettingsPanel Component
 * Settings page content extracted from HomePage for better organization
 *
 * @module components/home/SettingsPanel
 */

import React, { useRef, useState, useEffect } from 'react';
import { Icons } from '../../constants';
import { CanvasSettings, UserProfile, AppTheme } from '../../types';
import { supabase } from '../../services/supabase';
import { updateProfile } from '../../services/authService';

export interface SettingsPanelProps {
    userProfile?: UserProfile | null;
    onUpdateProfile?: (profile: Partial<UserProfile>) => void;
    canvasSettings: CanvasSettings;
    onCanvasSettingsChange: (settings: CanvasSettings) => void;
    currentTheme: AppTheme;
    onThemeChange: (theme: AppTheme) => void;
}

export const SettingsPanel = ({
    userProfile,
    onUpdateProfile,
    canvasSettings,
    onCanvasSettingsChange,
    currentTheme,
    onThemeChange,
}: SettingsPanelProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState(userProfile?.name || '');
    const [hasNameChanges, setHasNameChanges] = useState(false);

    // Sync name with userProfile when it changes
    useEffect(() => {
        setName(userProfile?.name || '');
        setHasNameChanges(false);
    }, [userProfile?.name]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value;
        setName(newName);
        setHasNameChanges(newName !== (userProfile?.name || ''));
    };

    const handleSaveName = async () => {
        if (!userProfile?.id || !name.trim()) return;

        setIsSaving(true);
        try {
            const result = await updateProfile(userProfile.id, { name: name.trim() });
            if (!result.success) throw (result as any).error;

            if (onUpdateProfile) {
                onUpdateProfile(result.data);
            }
            setHasNameChanges(false);
        } catch (error) {
            console.error('Error saving name:', error);
            alert('Failed to save name. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0 || !userProfile?.id) return;

        const file = e.target.files[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${userProfile.id}/${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        setIsUploading(true);

        try {
            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(filePath);

            const { error: dbError } = await supabase
                .from('profiles')
                .update({ avatar_url: publicUrl })
                .eq('id', userProfile.id);

            if (dbError) throw dbError;

            if (onUpdateProfile) {
                onUpdateProfile({ avatar: publicUrl });
            }
        } catch (error) {
            console.error('Error uploading avatar:', error);
            alert('Failed to upload profile picture. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="space-y-8">
                    {/* Profile Information */}
                    <div className="bg-white dark:bg-[#121212] p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                            <Icons.User className="w-6 h-6 text-slate-400" />
                            Profile Information
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-blue-400 to-indigo-500 p-[2px]">
                                    <img
                                        src={userProfile?.avatar || "https://picsum.photos/seed/user/100/100"}
                                        alt="user"
                                        className="w-full h-full object-cover rounded-full border-4 border-white dark:border-slate-900"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                    />
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="px-4 py-2 bg-slate-100 dark:bg-[#1a1a1a] hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold rounded-xl text-xs transition-colors disabled:opacity-50"
                                    >
                                        {isUploading ? 'Uploading...' : 'Change Photo'}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Full Name</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={handleNameChange}
                                        placeholder="Enter your name"
                                        className="flex-1 bg-slate-50 dark:bg-[#1a1a1a] border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors"
                                    />
                                    {hasNameChanges && (
                                        <button
                                            onClick={handleSaveName}
                                            disabled={isSaving || !name.trim()}
                                            className="px-4 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {isSaving ? (
                                                <span className="animate-spin">⏳</span>
                                            ) : (
                                                <Icons.Check className="w-4 h-4" />
                                            )}
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email</label>
                                <input
                                    type="email"
                                    defaultValue={userProfile?.email || "Guest User"}
                                    readOnly
                                    className="w-full bg-slate-50 dark:bg-[#1a1a1a] border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 font-bold text-slate-500 dark:text-slate-400 cursor-not-allowed"
                                />
                                <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
                            </div>
                        </div>
                    </div>

                    {/* Canvas Preferences */}
                    <div className="bg-white dark:bg-[#121212] p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                            <Icons.Maximize className="w-6 h-6 text-slate-400" />
                            Canvas Preferences
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800">
                                <div>
                                    <p className="font-bold text-slate-700 dark:text-slate-200">Snap to Grid</p>
                                    <p className="text-xs text-slate-500">Automatically align notes to a {canvasSettings.gridSize}px grid.</p>
                                </div>
                                <button
                                    onClick={() => onCanvasSettingsChange({ ...canvasSettings, snapToGrid: !canvasSettings.snapToGrid })}
                                    className={`w-12 h-7 rounded-full p-1 transition-colors ${canvasSettings.snapToGrid ? 'bg-indigo-500' : 'bg-slate-200 dark:bg-[#262626]'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${canvasSettings.snapToGrid ? 'translate-x-5' : ''}`}></div>
                                </button>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <p className="font-bold text-slate-700 dark:text-slate-200">Smart Alignment Guides</p>
                                    <p className="text-xs text-slate-500">Show Figma-style guides when notes align.</p>
                                </div>
                                <button
                                    onClick={() => onCanvasSettingsChange({ ...canvasSettings, showGuides: !canvasSettings.showGuides })}
                                    className={`w-12 h-7 rounded-full p-1 transition-colors ${canvasSettings.showGuides ? 'bg-indigo-500' : 'bg-slate-200 dark:bg-[#262626]'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${canvasSettings.showGuides ? 'translate-x-5' : ''}`}></div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-8">
                    {/* Appearance */}
                    <div className="bg-white dark:bg-[#121212] p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                            <Icons.Moon className="w-6 h-6 text-slate-400" />
                            Appearance
                        </h2>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold text-slate-700 dark:text-slate-200">Theme</p>
                                <p className="text-xs text-slate-500">Choose your preferred theme</p>
                            </div>
                            <div className="flex items-center bg-slate-200 dark:bg-[#262626] rounded-full p-1">
                                <button
                                    onClick={() => onThemeChange('system')}
                                    className={`p-2 rounded-full transition-all ${currentTheme === 'system' ? 'bg-white dark:bg-[#333] shadow-sm' : 'hover:bg-slate-300 dark:hover:bg-[#333]'}`}
                                    title="System"
                                >
                                    <Icons.Monitor className={`w-4 h-4 ${currentTheme === 'system' ? 'text-indigo-500' : 'text-slate-500 dark:text-slate-400'}`} />
                                </button>
                                <button
                                    onClick={() => onThemeChange('light')}
                                    className={`p-2 rounded-full transition-all ${currentTheme === 'light' ? 'bg-white dark:bg-[#333] shadow-sm' : 'hover:bg-slate-300 dark:hover:bg-[#333]'}`}
                                    title="Light"
                                >
                                    <Icons.Sun className={`w-4 h-4 ${currentTheme === 'light' ? 'text-yellow-500' : 'text-slate-500 dark:text-slate-400'}`} />
                                </button>
                                <button
                                    onClick={() => onThemeChange('dark')}
                                    className={`p-2 rounded-full transition-all ${currentTheme === 'dark' ? 'bg-white dark:bg-[#333] shadow-sm' : 'hover:bg-slate-300 dark:hover:bg-[#333]'}`}
                                    title="Dark"
                                >
                                    <Icons.Moon className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="bg-white dark:bg-[#121212] p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                            <Icons.Bell className="w-6 h-6 text-slate-400" />
                            Notifications
                        </h2>
                        <div className="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800">
                            <span className="font-medium text-slate-700 dark:text-slate-200">Email Alerts</span>
                            <div className="w-12 h-6 bg-green-500 rounded-full p-1 flex justify-end cursor-pointer">
                                <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                            </div>
                        </div>
                    </div>

                    {/* Data Management */}
                    <div className="bg-white dark:bg-[#121212] p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-red-500">
                            <Icons.Trash className="w-6 h-6" />
                            Data Management
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <p className="font-bold text-slate-700 dark:text-slate-200">Reset Application</p>
                                <p className="text-xs text-slate-500 mb-4">Clear all boards, preferences, and local data. This action cannot be undone.</p>
                                <button
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to reset the application? All your data will be permanently deleted.')) {
                                            localStorage.clear();
                                            const req = indexedDB.deleteDatabase('NotesScapeDB');
                                            req.onsuccess = () => window.location.reload();
                                            req.onerror = () => {
                                                console.error("Failed to delete database");
                                                window.location.reload();
                                            };
                                            req.onblocked = () => window.location.reload();
                                        }
                                    }}
                                    className="w-full py-3 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 font-bold rounded-xl transition-colors border border-red-100 dark:border-red-900/30"
                                >
                                    Delete All Data
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SettingsPanel;
